class HeaderSearch extends HTMLElement {
  constructor() {
    super();
    this.header = null;
    this.trigger = this.querySelector('[data-header-search-trigger]');
    this.expand = null;
    this.closeBtn = null;
    this.mobileModal = null;
    this.headerWrapper = this.closest('.header-wrapper');
    this.desktopQuery = window.matchMedia('(min-width: 990px)');

    this.onTriggerClick = this.onTriggerClick.bind(this);
    this.onCloseClick = this.onCloseClick.bind(this);
    this.onKeyUp = this.onKeyUp.bind(this);
    this.onDocumentClick = this.onDocumentClick.bind(this);
    this.onBreakpointChange = this.onBreakpointChange.bind(this);
    this.onSearchInput = this.onSearchInput.bind(this);
    this.onMobileSearchInput = this.onMobileSearchInput.bind(this);
    this.onMobileSearchReset = this.onMobileSearchReset.bind(this);
    this.onMobilePanelLayout = this.onMobilePanelLayout.bind(this);
  }

  connectedCallback() {
    this.header = this.closest('.header');
    this.expand = this.header?.querySelector('[data-header-search-expand]');
    this.closeBtn = this.querySelector('[data-header-search-close]');
    this.mobileModal = this.querySelector('.header__search-mobile');

    this.trigger?.addEventListener('click', this.onTriggerClick);
    this.desktopQuery.addEventListener('change', this.onBreakpointChange);

    if (this.expand && this.expand.dataset.searchBound !== 'true') {
      this.expand.dataset.searchBound = 'true';
      this.bindSuggestionPanel(this.expand, this.onSearchInput);
    }

    if (this.mobileModal && this.mobileModal.dataset.searchBound !== 'true') {
      this.mobileModal.dataset.searchBound = 'true';
      this.bindSuggestionPanel(this.mobileModal, this.onMobileSearchInput);

      const details = this.mobileModal.querySelector('details');
      if (details) {
        this.mobileDetailsObserver = new MutationObserver(() => {
          if (details.hasAttribute('open')) {
            this.closeMenuDrawerIfOpen();
            this.updateMobileSearchPanelTop();
            window.addEventListener('resize', this.onMobilePanelLayout);
            if (window.visualViewport) {
              window.visualViewport.addEventListener('resize', this.onMobilePanelLayout);
            }
            this.syncMobileSuggestionPanels();
            this.collapseMobileSearchInputSelection();
          } else {
            window.removeEventListener('resize', this.onMobilePanelLayout);
            if (window.visualViewport) {
              window.visualViewport.removeEventListener('resize', this.onMobilePanelLayout);
            }
            document.documentElement.style.removeProperty('--header-search-panel-top');
            this.resetMobileSearch();
            this.clearMenuOverlayIfClosed();
          }
        });
        this.mobileDetailsObserver.observe(details, { attributes: true, attributeFilter: ['open'] });
      }
    }

    this.closeBtn?.addEventListener('click', this.onCloseClick);
  }

  disconnectedCallback() {
    this.trigger?.removeEventListener('click', this.onTriggerClick);
    this.closeBtn?.removeEventListener('click', this.onCloseClick);
    this.desktopQuery.removeEventListener('change', this.onBreakpointChange);
    this.predictiveObserver?.disconnect();
    this.resultsObserver?.disconnect();
    this.mobilePredictiveObserver?.disconnect();
    this.mobileResultsObserver?.disconnect();
    this.mobileDetailsObserver?.disconnect();
    window.removeEventListener('resize', this.onMobilePanelLayout);
    if (window.visualViewport) {
      window.visualViewport.removeEventListener('resize', this.onMobilePanelLayout);
    }
    document.documentElement.style.removeProperty('--header-search-panel-top');

    this.closeDesktop(false);
  }

  onMobilePanelLayout() {
    this.updateMobileSearchPanelTop();
  }

  updateMobileSearchPanelTop() {
    if (this.isDesktop() || !this.mobileModal) return;

    const details = this.mobileModal.querySelector('details');
    if (!details?.hasAttribute('open')) return;

    const headerBar = document.querySelector('.section-header .header');
    const top = headerBar ? Math.round(headerBar.getBoundingClientRect().bottom) : 0;
    document.documentElement.style.setProperty('--header-search-panel-top', `${top}px`);
  }

  closeMenuDrawerIfOpen() {
    const headerDrawer = document.querySelector('header-drawer');
    const menuDetails = headerDrawer?.querySelector('details.menu-drawer-container');
    if (!menuDetails?.hasAttribute('open')) return;

    const summary = menuDetails.querySelector('summary');
    if (!summary) return;

    headerDrawer.closeMenuDrawer(new Event('close'), summary);
    summary.setAttribute('aria-expanded', 'false');

    const input = this.mobileModal?.querySelector('input[type="search"]');
    if (input) {
      window.requestAnimationFrame(() => input.focus());
    }
  }

  clearMenuOverlayIfClosed() {
    const menuDetails = document.querySelector('header-drawer details.menu-drawer-container');
    const summary = menuDetails?.querySelector('summary.header__icon--menu');
    if (!summary || menuDetails.hasAttribute('open')) return;

    summary.setAttribute('aria-expanded', 'false');
    document.querySelector('.section-header')?.classList.remove('menu-open');
  }

  collapseMobileSearchInputSelection() {
    const input = this.mobileModal?.querySelector('input[type="search"]');
    if (!input?.value) return;

    const end = input.value.length;
    window.requestAnimationFrame(() => input.setSelectionRange(end, end));
  }

  bindSuggestionPanel(root, inputHandler) {
    const input = root.querySelector('input[type="search"]');
    const form = input?.form;
    input?.addEventListener('input', inputHandler);

    if (root === this.mobileModal) {
      form?.addEventListener('reset', this.onMobileSearchReset);
      root.querySelector('button[type="reset"]')?.addEventListener('click', this.onMobileSearchReset);
    }

    const predictiveSearch = root.querySelector('predictive-search');
    if (!predictiveSearch) return;

    const observer = new MutationObserver(() => {
      if (root === this.expand) {
        this.syncSuggestionPanels();
      } else {
        this.syncMobileSuggestionPanels();
      }
    });
    observer.observe(predictiveSearch, { attributes: true, attributeFilter: ['open', 'loading'] });

    const resultsContainer = root.querySelector('[data-predictive-search]');
    if (resultsContainer) {
      const resultsObserver = new MutationObserver(() => {
        if (root === this.expand) {
          this.syncSuggestionPanels();
        } else {
          this.syncMobileSuggestionPanels();
        }
      });
      resultsObserver.observe(resultsContainer, { childList: true, subtree: true });

      if (root === this.expand) {
        this.predictiveObserver = observer;
        this.resultsObserver = resultsObserver;
      } else {
        this.mobilePredictiveObserver = observer;
        this.mobileResultsObserver = resultsObserver;
      }
    } else if (root === this.expand) {
      this.predictiveObserver = observer;
    } else {
      this.mobilePredictiveObserver = observer;
    }
  }

  isDesktop() {
    return this.desktopQuery.matches;
  }

  onTriggerClick(event) {
    if (!this.isDesktop()) return;
    event.preventDefault();
    this.openDesktop();
  }

  onCloseClick(event) {
    event.preventDefault();
    event.stopPropagation();
    this.closeDesktop();
  }

  onKeyUp(event) {
    if (event.code === 'Escape') this.closeDesktop();
  }

  onDocumentClick(event) {
    if (!this.headerWrapper?.classList.contains('header-search-active')) return;
    if (this.expand?.contains(event.target) || this.trigger?.contains(event.target) || this.closeBtn?.contains(event.target)) return;
    this.closeDesktop();
  }

  onBreakpointChange() {
    if (!this.isDesktop()) this.closeDesktop(false);
  }

  onSearchInput() {
    this.syncSuggestionPanels();
  }

  onMobileSearchInput() {
    this.syncMobileSuggestionPanels();
  }

  onMobileSearchReset(event) {
    if (this.isDesktop() || !this.mobileModal) return;

    event?.stopPropagation?.();

    const predictiveSearch = this.mobileModal.querySelector('predictive-search');
    const live = this.mobileModal.querySelector('[data-predictive-search]');

    if (predictiveSearch?.close) {
      predictiveSearch.close(true);
    }

    this.clearMobileLiveResults(live);

    window.requestAnimationFrame(() => {
      this.syncMobileSuggestionPanels();
      const input = this.mobileModal?.querySelector('input[type="search"]');
      input?.focus();
      if (input?.value) input.setSelectionRange(input.value.length, input.value.length);
    });
  }

  clearMobileLiveResults(live) {
    if (!live) return;
    live.querySelector('#predictive-search-results')?.remove();
    live.setAttribute('hidden', '');
  }

  hasLiveProductResults(live) {
    return Boolean(live?.querySelector('.predictive-search__item--compact'));
  }

  syncSuggestionPanels() {
    const input = this.expand?.querySelector('input[type="search"]');
    const defaults = this.expand?.querySelector('[data-header-search-defaults]');
    const live = this.expand?.querySelector('[data-predictive-search]');
    const noResults = this.expand?.querySelector('[data-header-search-no-results]');
    const predictiveSearch = this.expand?.querySelector('predictive-search');
    const isActive = this.headerWrapper?.classList.contains('header-search-active');
    const query = input?.value.trim() || '';
    const hasQuery = Boolean(query);
    const liveOpen = predictiveSearch?.hasAttribute('open');
    const isLoading = predictiveSearch?.hasAttribute('loading');
    const hasProducts = this.hasLiveProductResults(live);
    const showLive = hasQuery && liveOpen && hasProducts;
    const showNoResults = hasQuery && liveOpen && !isLoading && !hasProducts;

    if (defaults) {
      const hideDefaults = !isActive || hasQuery || liveOpen || showLive;
      defaults.toggleAttribute('hidden', hideDefaults);
    }

    if (live) {
      live.toggleAttribute('hidden', !showLive);
      if (!showLive) live.style.removeProperty('max-height');
    }

    if (noResults) {
      if (showNoResults) {
        noResults.textContent = noResults.dataset.noResultsTemplate?.replace('__QUERY__', query) || `No results found for "${query}"`;
      }
      noResults.toggleAttribute('hidden', !showNoResults);
    }

    const intro = this.expand?.querySelector('[data-header-search-intro]');
    if (intro) {
      const showIntro =
        isActive &&
        ((defaults && !defaults.hasAttribute('hidden')) ||
          (live && !live.hasAttribute('hidden')) ||
          (noResults && !noResults.hasAttribute('hidden')));
      intro.toggleAttribute('hidden', !showIntro);
    }

    window.requestAnimationFrame(() => this.updateIntroLayout());
  }

  updateIntroLayout() {
    const form = this.expand?.querySelector('.header-search-expand__form');
    const intro = this.expand?.querySelector('[data-header-search-intro]');
    if (!form) return;

    if (intro && !intro.hasAttribute('hidden')) {
      form.style.setProperty('--header-search-intro-height', `${intro.offsetHeight}px`);
    } else {
      form.style.removeProperty('--header-search-intro-height');
    }
  }

  syncMobileSuggestionPanels() {
    const details = this.mobileModal?.querySelector('details');
    const input = this.mobileModal?.querySelector('input[type="search"]');
    const defaults = this.mobileModal?.querySelector('[data-header-search-defaults]');
    const live = this.mobileModal?.querySelector('[data-predictive-search]');
    const scrollResults = this.mobileModal?.querySelector('[data-header-search-mobile-results]');
    const predictiveSearch = this.mobileModal?.querySelector('predictive-search');
    const isOpen = details?.hasAttribute('open');
    const query = input?.value.trim() || '';
    const hasQuery = Boolean(query);

    if (!hasQuery && predictiveSearch?.hasAttribute('open')) {
      predictiveSearch.close(false);
      this.clearMobileLiveResults(live);
    }

    const liveOpen = predictiveSearch?.hasAttribute('open');
    const isLoading = predictiveSearch?.hasAttribute('loading');
    const hasProducts = this.hasLiveProductResults(live);
    const showLive = hasQuery && liveOpen && (hasProducts || isLoading);
    const showDefaults = isOpen && !hasQuery && Boolean(defaults);

    if (defaults) {
      defaults.toggleAttribute('hidden', !showDefaults);
    }

    if (live) {
      live.toggleAttribute('hidden', !showLive);
      if (!showLive) live.style.removeProperty('max-height');
    }

    if (scrollResults) {
      scrollResults.toggleAttribute('hidden', !(showDefaults || showLive));
    }

    window.requestAnimationFrame(() => this.updateMobileSearchPanelTop());
  }

  resetMobileSearch() {
    const defaults = this.mobileModal?.querySelector('[data-header-search-defaults]');
    const live = this.mobileModal?.querySelector('[data-predictive-search]');
    const scrollResults = this.mobileModal?.querySelector('[data-header-search-mobile-results]');
    const predictiveSearch = this.mobileModal?.querySelector('predictive-search');

    defaults?.setAttribute('hidden', '');
    scrollResults?.setAttribute('hidden', '');
    this.clearMobileLiveResults(live);
    if (predictiveSearch?.close) predictiveSearch.close(true);
  }

  openDesktop() {
    if (!this.expand || !this.headerWrapper) return;

    this.headerWrapper.classList.add('header-search-active');
    this.expand.removeAttribute('hidden');
    this.trigger?.setAttribute('aria-expanded', 'true');
    this.trigger?.setAttribute('hidden', '');
    this.closeBtn?.removeAttribute('hidden');
    this.closeBtn?.setAttribute('aria-hidden', 'false');
    this.expand.setAttribute('aria-hidden', 'false');

    document.addEventListener('keyup', this.onKeyUp);
    document.addEventListener('click', this.onDocumentClick);

    this.syncSuggestionPanels();

    const input = this.expand.querySelector('input[type="search"]');
    if (input) window.requestAnimationFrame(() => input.focus());
  }

  closeDesktop(focusTrigger = true) {
    if (!this.headerWrapper) return;

    this.headerWrapper.classList.remove('header-search-active');
    this.expand?.setAttribute('hidden', '');
    this.expand?.setAttribute('aria-hidden', 'true');

    this.header?.querySelectorAll('header-search').forEach((searchEl) => {
      const trigger = searchEl.querySelector('[data-header-search-trigger]');
      const closeBtn = searchEl.querySelector('[data-header-search-close]');
      trigger?.setAttribute('aria-expanded', 'false');
      trigger?.removeAttribute('hidden');
      closeBtn?.setAttribute('hidden', '');
      closeBtn?.setAttribute('aria-hidden', 'true');
    });

    document.removeEventListener('keyup', this.onKeyUp);
    document.removeEventListener('click', this.onDocumentClick);

    const predictiveSearch = this.expand?.querySelector('predictive-search');
    if (predictiveSearch?.close) predictiveSearch.close(true);

    const defaults = this.expand?.querySelector('[data-header-search-defaults]');
    defaults?.setAttribute('hidden', '');

    const noResults = this.expand?.querySelector('[data-header-search-no-results]');
    noResults?.setAttribute('hidden', '');

    this.expand?.querySelector('.header-search-expand__form')?.style.removeProperty('--header-search-intro-height');

    const input = this.expand?.querySelector('input[type="search"]');
    if (input) input.value = '';

    if (focusTrigger && this.isDesktop()) this.trigger?.focus();
  }
}

customElements.define('header-search', HeaderSearch);
